import React from "react";
import Chart from "react-apexcharts";
import { FaCalendarAlt, FaFilter } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { useAnalytics } from "../../customHooks/Analytics/useAnalytics";
import { useMemo } from "react";

const Analytics = () => {
  const {
    loading,
    comparisonChartData,
    settlementChartData,
    selectedBuyer,
    setSelectedBuyer,
    startMonth,
    setStartMonth,
    endMonth,
    setEndMonth,
    availableBuyers,
    handleRefresh,
  } = useAnalytics();

  // Get current month in YYYY-MM format to restrict future dates
  const currentDate = new Date();
  const maxMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  const currentYear = currentDate.getFullYear();
  const currentMonthNum = currentDate.getMonth() + 1;

  // Generate year options (from 2010 to current year)
  const yearOptions = Array.from(
    { length: currentYear - 2020 + 1 },
    (_, i) => 2020 + i,
  );

  // Generate month options based on selected year
  const getMonthOptions = (year) => {
    const maxMonthForYear = year === currentYear ? currentMonthNum : 12;
    return Array.from({ length: maxMonthForYear }, (_, i) => i + 1);
  };

  // Parse and update start month
  const handleStartYearChange = (year) => {
    const month = startMonth.split("-")[1] || "01";
    setStartMonth(`${year}-${month}`);
  };

  const handleStartMonthChange = (month) => {
    const year = startMonth.split("-")[0] || currentYear;
    setStartMonth(`${year}-${String(month).padStart(2, "0")}`);
  };

  // Parse and update end month
  const handleEndYearChange = (year) => {
    const month = endMonth.split("-")[1] || "01";
    setEndMonth(`${year}-${month}`);
  };

  const handleEndMonthChange = (month) => {
    const year = endMonth.split("-")[0] || currentYear;
    setEndMonth(`${year}-${String(month).padStart(2, "0")}`);
  };

  const startYear = startMonth.split("-")[0] || "";
  const startMonthNum = startMonth ? parseInt(startMonth.split("-")[1]) : "";
  const endYear = endMonth.split("-")[0] || "";
  const endMonthNum = endMonth ? parseInt(endMonth.split("-")[1]) : "";

  const comparisonOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#ef5350", "#fb8c00", "#00acc1"],
    xaxis: {
      categories: comparisonChartData.categories,
      title: { text: "Month/Year" },
    },
    yaxis: { title: { text: "Order Count" } },
    legend: { position: "top" },
  };

  const settlementOptions = useMemo(
    () => ({
      chart: {
        height: 400,
        type: "line",
        stacked: false,
        toolbar: { show: false },
      },
      colors: ["#2e5bff", "#00acc1", "#fb8c00"],
      // stroke array matches series order: [Order Count, Requested, Settled]
      stroke: { width: [4, 1, 1], curve: "smooth" },
      markers: {
        size: [8, 0, 0],
        strokeWidth: [2, 0, 0],
        hover: { size: [10, 0, 0] },
      },
      xaxis: {
        categories: settlementChartData.categories,
        title: { text: "Month/Year" },
      },
      yaxis: (function () {
        const orderSeries = settlementChartData?.series?.[0] || {};
        const orderData = Array.isArray(orderSeries.data)
          ? orderSeries.data
          : [];
        const orderMax = orderData.length ? Math.max(...orderData) : 0;
        const orderMaxScaled = Math.max(5, Math.ceil(orderMax * 1.2));

        return [
          {
            // LEFT axis → Order Count (first series)
            seriesName: "Order Count",
            title: { text: "Order Count" },
            min: 0,
            max: orderMaxScaled,
            tickAmount: Math.min(
              6,
              Math.max(2, Math.ceil(orderMaxScaled / 10)),
            ),
            labels: { formatter: (v) => v.toFixed(0) },
          },
          {
            // RIGHT axis → Amounts (both Requested and Settled share this axis)
            seriesName: "Amounts",
            opposite: true,
            title: { text: "Amount (₹)" },
            labels: { formatter: (v) => v.toFixed(0) },
          },
        ];
      })(),
      legend: { position: "top" },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0],
        offsetY: -6,
        background: {
          enabled: false,
        },
        style: {
          colors: ["#3073ca"],
          fontSize: "12px",
          fontWeight: "600",
        },
      },
      tooltip: {
        fixed: { enabled: true, position: "topLeft", offsetY: 30, offsetX: 60 },
      },
      fill: { opacity: [1, 1, 1] },
      responsive: [{ breakpoint: 768, options: { chart: { height: 300 } } }],
    }),
    [settlementChartData],
  );

  // Check if all settlement data is zero
  const hasSettlementData = useMemo(() => {
    return (
      settlementChartData?.series?.some(
        (series) =>
          Array.isArray(series.data) && series.data.some((val) => val > 0),
      ) || false
    );
  }, [settlementChartData]);

  // Check if all comparison data is zero
  const hasComparisonData = useMemo(() => {
    return (
      comparisonChartData?.series?.some(
        (series) =>
          Array.isArray(series.data) && series.data.some((val) => val > 0),
      ) || false
    );
  }, [comparisonChartData]);

  const NoDataComponent = () => (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        height: "400px",
        width: "100%",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
      }}
    >
      <p className="text-muted mb-0 fs-5">No data available</p>
    </div>
  );

  return (
    <div className="container-fluid px-4 py-3">
      {/* Filter Card */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap py-3">
          <h4 className="fw-bold mb-0">Buyer App Analytics</h4>
          <div className="d-flex gap-3">
            <div className="d-flex align-items-center gap-2 border rounded px-3 py-1 bg-light">
              <FaFilter />
              <select
                className="form-select border-0 bg-transparent"
                value={selectedBuyer}
                onChange={(e) => setSelectedBuyer(e.target.value)}
                style={{
                  paddingRight: "2.5rem",
                  backgroundPosition: "right 0.6rem center",
                  textOverflow: "ellipsis",
                }}
              >
                {availableBuyers.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="d-flex align-items-center gap-2 border rounded px-2 py-2 bg-light"
              style={{ flexWrap: "wrap" }}
            >
              <FaCalendarAlt style={{ color: "#6c757d", marginRight: "4px" }} />
              <select
                className="form-select form-select-sm border-0 bg-transparent"
                value={startYear}
                onChange={(e) => handleStartYearChange(e.target.value)}
                style={{
                  width: "75px",
                  fontSize: "13px",
                }}
              >
                <option value="">YYYY</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                className="form-select form-select-sm border-0 bg-transparent"
                value={String(startMonthNum) || ""}
                onChange={(e) => handleStartMonthChange(e.target.value)}
                style={{ width: "65px", fontSize: "13px" }}
              >
                <option value="">MM</option>
                {startYear &&
                  getMonthOptions(parseInt(startYear)).map((m) => (
                    <option key={m} value={String(m)}>
                      {String(m).padStart(2, "0")}
                    </option>
                  ))}
              </select>
              <span
                style={{ color: "#6c757d", fontWeight: "500", margin: "0 1px" }}
              >
                to
              </span>
              <select
                className="form-select form-select-sm border-0 bg-transparent"
                value={endYear}
                onChange={(e) => handleEndYearChange(e.target.value)}
                style={{ width: "75px", fontSize: "13px" }}
              >
                <option value="">YYYY</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                className="form-select form-select-sm border-0 bg-transparent"
                value={String(endMonthNum) || ""}
                onChange={(e) => handleEndMonthChange(e.target.value)}
                style={{ width: "65px", fontSize: "13px" }}
              >
                <option value="">MM</option>
                {endYear &&
                  getMonthOptions(parseInt(endYear)).map((m) => (
                    <option key={m} value={String(m)}>
                      {String(m).padStart(2, "0")}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="row">
          {/* Chart 1 */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="fw-bold mb-3">Buyer Order Comparison</h5>
                {hasComparisonData ? (
                  <Chart
                    options={comparisonOptions}
                    series={comparisonChartData.series}
                    type="bar"
                    height={400}
                  />
                ) : (
                  <NoDataComponent />
                )}
              </div>
            </div>
          </div>

          {/* Chart 2 */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">
                    Settlement Analysis ({selectedBuyer.split(/[./]/)[0]})
                  </h5>
                  <button
                    onClick={handleRefresh}
                    className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                    style={{
                      padding: "4px 10px",
                      fontSize: "13px",
                    }}
                    disabled={loading}
                  >
                    <MdRefresh size={16} />
                    Refresh
                  </button>
                </div>
                {hasSettlementData ? (
                  <Chart
                    options={settlementOptions}
                    series={settlementChartData.series}
                    type="line"
                    height={400}
                  />
                ) : (
                  <NoDataComponent />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
